import React, {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  AddressCompleteSearchProvider,
  ValidatedFormFieldConfig,
} from "@evergiving/common/dist/es2015/form";
import classNames from "classnames";
import { useDispatch } from "react-redux";
import { setFormValue } from "@/lib/redux/actions";
import { RootState, VersionedStringFormState } from "@/lib/redux/types";

import { useAppSelector } from "@/lib/hooks/redux";
import { useShowErrors } from "@/lib/hooks/useShowErrors";
import { FieldError } from "@/lib/components/FieldError";
import { useFieldValidationErrors } from "@/lib/hooks/useFieldValidationErrors";

import { normalizeFieldValue } from "@/lib/misc/validations";

import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";
import { Spinner } from "@/lib/components/Spinner";

const fetchCitySuggestion = async (
  query: string,
  cityCompletionEndpointUrl: string,
  currentCountryCode: string
) => {
  const searchUrl = new URL(cityCompletionEndpointUrl);

  const url = searchUrl;

  url.search = new URLSearchParams({
    query,
    maxNumRecords: String(10),
    countryCode: currentCountryCode,
    searchProvider: AddressCompleteSearchProvider.Mapbox,
  }).toString();

  try {
    const res = await fetch(url.toString());

    if (!res.ok) {
      return {
        statusCode: res.status,
      };
    }

    const results = await res.json();

    return results;
  } catch {
    return { error: "Unable to retrieve cities" };
  }
};

interface CityAutocompleteFieldProps {
  fieldName: keyof VersionedStringFormState;
  config: ValidatedFormFieldConfig;
  cityCompletionEndpointUrl: string;
  currentCountryCode: string;
}

export const CityAutocompleteField = ({
  fieldName,
  config,
  cityCompletionEndpointUrl,
  currentCountryCode,
}: CityAutocompleteFieldProps) => {
  const required = !!config.requiredFieldErrorText;
  const valueFromStore = useAppSelector(
    (state: RootState) => state[fieldName].value
  );

  const [currentValue, setCurrentValue] = useState<string>(
    valueFromStore || ""
  );
  const [autocompleteCities, setAutocompleteCities] = useState<
    Array<{ name: string; mapboxId: string }>
  >([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState(false);

  const fieldValidationErrors = useFieldValidationErrors(config);
  const anyErrors = useShowErrors(config, fieldName, currentValue);
  const errorId = `${fieldName}-errors`;

  const dispatch = useDispatch();

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const selectedCity = normalizeFieldValue(
        e.currentTarget.textContent || ""
      );

      setAutocompleteCities([]);
      if (selectedCity !== "") {
        dispatch(
          setFormValue(
            fieldName,
            selectedCity,
            fieldValidationErrors.forValue(selectedCity).length > 0
          )
        );
      }
    },
    [dispatch, fieldName, fieldValidationErrors]
  );

  useEffect(() => {
    setCurrentValue(valueFromStore || "");
  }, [valueFromStore, setCurrentValue]);

  const handleCityChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    setCurrentValue(e.target.value);
    setIsDirty(true);

    if (!currentValue) return;

    const res = await fetchCitySuggestion(
      currentValue,
      cityCompletionEndpointUrl,
      currentCountryCode
    );

    if (res.suggestions) {
      setAutocompleteCities(
        res.suggestions.map((result: { name: string; mapbox_id: string }) => ({
          name: result.name,
          mapboxId: result.mapbox_id,
        }))
      );
    }

    if (res.error) {
      console.error(res.error);
    }

    setLoading(false);
  };

  return (
    <LabelledField id={fieldName}>
      <LabelText requiredIndicator={required}>{config.labelText}</LabelText>
      <div
        className="addressEntry"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded
        aria-owns="cities-list"
        aria-controls="cities-list"
      >
        <div className="spinner" aria-live="polite" role="status">
          <Spinner
            className={loading ? "spinnerVisible" : "spinnerInvisible"}
          />
          {loading && (
            <span className="screenReaderOnly">Loading cities suggestions</span>
          )}
        </div>

        <input
          id="city"
          name={fieldName}
          type="text"
          className="textField"
          placeholder={config.placeholderText}
          aria-autocomplete="list"
          onChange={handleCityChange}
          value={currentValue}
          aria-required={required}
          aria-invalid={(anyErrors && isDirty) || undefined}
          aria-describedby={anyErrors ? errorId : undefined}
        />
        <ul id="cities-list">
          {autocompleteCities.map(({ name, mapboxId }) => (
            <li key={mapboxId}>
              <button
                type="button"
                className={classNames("addressChoice", "addressMatch")}
                onClick={handleClick}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      </div>
      {/* eslint-enable jsx-a11y/no-autofocus */}
      <div role="alert">
        {anyErrors && isDirty ? (
          <FieldError
            id={errorId}
            messages={fieldValidationErrors.forValue(
              normalizeFieldValue(currentValue)
            )}
          />
        ) : null}
      </div>
    </LabelledField>
  );
};
